import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { doc, setDoc, serverTimestamp, collection, query, where, getDocs, updateDoc, arrayUnion, increment, limit, addDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { initializeUserWallets } from '../services/walletService';
import { createUserWithEmailAndPassword, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth } from '../services/firebase';

// Generate referral code based on user ID
const generateReferralCode = (uid) => {
  // Take first 6 characters of UID and add random 4 characters
  const prefix = uid.substring(0, 6);
  const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();
  return `${prefix}${randomChars}`;
};

const FinishSignIn = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  
  const { confirmSignInWithEmailLink, checkIfEmailLink } = useAuth();
  const navigate = useNavigate();
  
  useEffect(() => {
    const completeSignIn = async () => {
      // Check for URL mode
      const urlParams = new URLSearchParams(window.location.search);
      const mode = urlParams.get('mode');
      const oobCode = urlParams.get('oobCode');
      
      // Check if this is a sign-in link
      const isEmailLink = checkIfEmailLink(window.location.href);
      
      if (!isEmailLink) {
        setError('Invalid sign-in link. This does not appear to be a valid email sign-in link.');
        setLoading(false);
        return;
      }
      
      // Get the email from localStorage
      let emailForSignIn = window.localStorage.getItem('emailForSignIn');
      
      // If email is not found in localStorage, ask user to provide it
      if (!emailForSignIn) {
        const providedEmail = window.prompt('Please provide your email for confirmation');
        
        if (!providedEmail) {
          setError('Email is required to complete sign-in');
          setLoading(false);
          return;
        }
        
        emailForSignIn = providedEmail;
      }
      
      setEmail(emailForSignIn);
      
      try {
        // Get the registration data from localStorage
        const username = window.localStorage.getItem('registrationUsername');
        const referralCode = window.localStorage.getItem('registrationReferralCode');
        const password = window.localStorage.getItem('registrationPassword');
        
        let result;
        
        // Check if this is a new registration with a password
        if (username && password) {
          try {
            // Create the user with email and password
            result = await createUserWithEmailAndPassword(auth, emailForSignIn, password);
          } catch (passwordAuthError) {
            // If account already exists, try email link instead
            if (passwordAuthError.code === 'auth/email-already-in-use') {
              result = await confirmSignInWithEmailLink(emailForSignIn, window.location.href);
              
              // If sign-in successful and we have a password, update the user's password
              if (result && result.user && password) {
                try {
                  await updatePassword(result.user, password);
                } catch (updatePasswordError) {
                  // If user needs to reauthenticate first
                  if (updatePasswordError.code === 'auth/requires-recent-login') {
                    try {
                      // Create credential and reauthenticate
                      const credential = EmailAuthProvider.credentialWithLink(
                        emailForSignIn, 
                        window.location.href
                      );
                      await reauthenticateWithCredential(result.user, credential);
                      
                      // Try updating password again
                      await updatePassword(result.user, password);
                    } catch (reauthError) {
                      // Continue without password update
                    }
                  }
                }
              }
            } else {
              throw passwordAuthError;
            }
          }
        } else {
          // Regular email link sign-in
          result = await confirmSignInWithEmailLink(emailForSignIn, window.location.href);
          
          // If we have a password in localStorage but this isn't a new registration,
          // it might be from a previous attempt - try to set the password anyway
          if (result && result.user && password && !username) {
            try {
              await updatePassword(result.user, password);
            } catch (setPasswordError) {
              // Continue without password update
            }
          }
        }
        
        // If this is a new user and we have a username, set up their profile
        if (result && result.user && username) {
          // Create user reference once, outside of conditionals
          const userRef = doc(db, 'users', result.user.uid);
          const newReferralCode = generateReferralCode(result.user.uid);
          
          // Check again if the username is unique (for double safety)
          const usersRef = collection(db, 'users');
          const q = query(usersRef, where('username', '==', username), limit(1));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            // Username is already taken, generate a unique username
            const uniqueUsername = `${username}${Math.floor(Math.random() * 10000)}`;
            
            await setDoc(userRef, {
              email: result.user.email,
              username: uniqueUsername, // Use the unique username
              displayName: uniqueUsername,
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp(),
              referralCode: newReferralCode,
              originalUsernameWanted: username // Store the original username they wanted
            }, { merge: true });
          } else {
            // Username is available, proceed as normal
            await setDoc(userRef, {
              email: result.user.email,
              username: username,
              displayName: username,
              createdAt: serverTimestamp(),
              lastLogin: serverTimestamp(),
              referralCode: newReferralCode
            }, { merge: true });
          }
          
          // Initialize wallet for new user
          await initializeUserWallets(result.user.uid);
          
          // Process referral code if provided
          if (referralCode) {
            try {
              console.log('Processing referral code in FinishSignIn:', referralCode);
              // Find the referrer based on the code
              const usersRef = collection(db, 'users');
              const q = query(usersRef, where('referralCode', '==', referralCode));
              const querySnapshot = await getDocs(q);
              
              if (!querySnapshot.empty) {
                const referrerDoc = querySnapshot.docs[0];
                const referrerUID = referrerDoc.id;
                console.log('Found referrer:', referrerUID);
                
                // Update current user document with referrer info
                await updateDoc(userRef, {
                  referrerUID: referrerUID
                });
                console.log('Updated user document with referrerUID');
                
                // Update referrer's wallet and stats
                const REFERRAL_REWARD = 1000; // Fixed reward amount
                const referrerData = referrerDoc.data();
                const currentWalletA = referrerData.walletA || 0;
                const currentWalletSources = referrerData.walletSources || {};
                const currentReferralEarnings = currentWalletSources.referrals || 0;
                
                // Check for milestone bonus (every 5 referrals)
                const allReferralsQ = query(usersRef, where('referrerUID', '==', referrerUID));
                const allReferralsSnapshot = await getDocs(allReferralsQ);
                const currentReferralCount = allReferralsSnapshot.size;
                const newReferralCount = currentReferralCount + 1;
                const milestoneBonus = newReferralCount % 5 === 0 ? REFERRAL_REWARD : 0;
                const totalReward = REFERRAL_REWARD + milestoneBonus;
                
                console.log('Current referral count:', currentReferralCount);
                console.log('New referral count:', newReferralCount);
                console.log('Milestone bonus:', milestoneBonus);
                console.log('Total reward:', totalReward);
                
                // Update referrer's wallet and stats
                await updateDoc(doc(db, 'users', referrerUID), {
                  walletA: currentWalletA + totalReward,
                  'walletSources.referrals': currentReferralEarnings + totalReward,
                  totalReferrals: increment(1),
                  referredUsers: arrayUnion(result.user.uid),
                  totalReferralEarnings: increment(totalReward)
                });
                console.log('Updated referrer wallet and stats');
                
                // Create the transaction record for the referrer
                try {
                  console.log('Adding referral transaction');
                  const transactionsRef = collection(db, 'users', referrerUID, 'transactions');
                  await addDoc(transactionsRef, {
                    type: 'earn',
                    amount: REFERRAL_REWARD,
                    source: 'referrals',
                    description: 'Referral bonus',
                    timestamp: serverTimestamp(),
                    referredUser: {
                      uid: result.user.uid,
                      displayName: username || 'New User',
                      email: result.user.email,
                      username: username || result.user.email.split('@')[0]
                    }
                  });
                  console.log('Added referral transaction with referredUser info');
                  
                  // If there's a milestone bonus, create a separate transaction for clarity
                  if (milestoneBonus > 0) {
                    await addDoc(transactionsRef, {
                      type: 'earn',
                      amount: milestoneBonus,
                      source: 'referrals',
                      description: 'Referral milestone bonus (5 referrals)',
                      timestamp: serverTimestamp(),
                      milestone: newReferralCount
                    });
                    console.log('Added milestone bonus transaction');
                  }
                } catch (txError) {
                  console.error('Error creating referral transaction:', txError);
                  // We've already updated the wallet, so continue anyway
                }
                
                // Add a notification for the referrer about the new referral
                try {
                  const notificationRef = collection(db, 'users', referrerUID, 'notifications');
                  await addDoc(notificationRef, {
                    type: 'referral',
                    message: 'You have a new referral!',
                    timestamp: serverTimestamp(),
                    read: false,
                    referredUser: {
                      uid: result.user.uid,
                      displayName: username || 'New User',
                      email: result.user.email,
                      username: username || result.user.email.split('@')[0]
                    }
                  });
                  console.log('Added notification for referrer');
                } catch (err) {
                  console.error('Error adding notification:', err);
                  // Continue anyway - this is not critical
                }
                
                // Add a self-transaction in the current user's account too
                try {
                  const myTransactionsRef = collection(db, 'users', result.user.uid, 'transactions');
                  await addDoc(myTransactionsRef, {
                    type: 'info',
                    source: 'referrals',
                    description: 'You were referred by another user',
                    timestamp: serverTimestamp(),
                    referredBy: referrerUID
                  });
                  console.log('Added self-transaction to track being referred');
                } catch (err) {
                  console.error('Error adding self-transaction:', err);
                  // Continue anyway - this is not critical
                }
              } else {
                console.log('No user found with referral code:', referralCode);
              }
            } catch (referralError) {
              console.error('Error processing referral:', referralError);
              // Continue even if referral processing fails
            }
          }
          
          // Clear registration data from localStorage
          window.localStorage.removeItem('registrationUsername');
          window.localStorage.removeItem('registrationReferralCode');
          window.localStorage.removeItem('registrationPassword');
        }
        
        setSuccess(true);
        
        // Redirect to home page after a short delay
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    completeSignIn();
  }, [confirmSignInWithEmailLink, checkIfEmailLink, navigate]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="px-6 py-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">
              {loading ? 'Completing Sign In...' : success ? 'Sign In Complete!' : 'Sign In Failed'}
            </h2>
            
            {loading && (
              <div className="flex justify-center my-6">
                <svg className="animate-spin h-10 w-10 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
            
            {success && (
              <div className="text-green-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="text-md text-gray-600 mt-4">
                  You've successfully signed in with {email}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Redirecting you to the homepage...
                </p>
              </div>
            )}
            
            {error && (
              <div className="text-red-600 mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-md text-red-600 mt-4">{error}</p>
                <button
                  onClick={() => navigate('/register')}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Back to Registration
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinishSignIn;